import { useEffect, useState, useMemo, useCallback } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Box,
  IconButton,
  Snackbar,
  Grid,
  Button,
  ThemeProvider,
  CssBaseline,
  Tooltip,
  Select,
  Option,
} from '@mui/joy';
import {
  Delete as DeleteIcon,
  Edit,
  KeyboardArrowRight,
  Add,
  ContentCopy,
  UploadFile,
  ExpandMore,
  ExpandLess,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import Link from '@mui/joy/Link';
import { useNavigate, useLocation } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { listPricingRules, deletePricingRule } from '../../main/API/pricingRule';
import CustomTextField from '../../main/widgets/CustomTextField';
import ShipmentUnitOptionList from '../../main/widgets/ShipmentUnitOptionList';
import ShipmentTypeOptionList from '../../main/widgets/ShipmentTypeOptionList';
import CustomerOptionList from '../../main/widgets/CustomersOptionList';
import PricingPlanOptionList from '../../main/widgets/PricingPlanOptionList';
import SettlementGroupOptionList from '../../main/widgets/SettlementGroupOptionList';
import DeleteAlert from '../../services/services_manager/views/widgets/DeleteAlert';
import DuplicatePricingRuleDialog from './widgets/DuplicatePricingRuleDialog';
import { theme } from '../../main/theme/constants';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { masterApiErrorMessage } from '../../main/utils/masterApiError';
import { OmrAmount } from '../../main/components/OmrAmount';

/** v6: `valueGetter(params)` only. v7+: `(value, row, column, apiRef)`. */
function valueGetterRow(arg1, arg2) {
  if (arg2 !== undefined) return arg2;
  return arg1?.row;
}

function dashIfEmpty(value) {
  if (value == null || value === '') return '—';
  return value;
}

function formatCustomerRef(customer) {
  if (customer == null) return '—';
  if (typeof customer === 'object') {
    const id = customer.id;
    const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim();
    if (id != null && name) return `${id} (${name})`;
    if (id != null) return String(id);
    return '—';
  }
  return String(customer);
}

function formatShipmentTypeRef(row) {
  if (!row) return '—';
  const apiName = row.shipment_type_name;
  if (apiName != null && String(apiName).trim() !== '') return String(apiName).trim();

  const shipmentType = row.shipment_type;
  if (shipmentType == null) return '—';
  if (typeof shipmentType === 'object') {
    const name = shipmentType.name || shipmentType.title || shipmentType.label;
    if (name) return String(name);
    if (shipmentType.id != null) return String(shipmentType.id);
    return '—';
  }
  return String(shipmentType);
}

function formatPercent(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? `${n}%` : String(value);
}

// Same strategy slugs the rule form offers.
const STRATEGY_OPTIONS = [
  'flat_per_shipment',
  'per_unit_weight',
  'meter_per_line',
  'meter_per_line_ceil',
  'group_total_lookup',
  'flat_addon',
  'percent_addon',
];

// Send a boolean query param only when the user picked yes or no.
function setBooleanParam(params, key, value) {
  if (value === 'true' || value === 'false') {
    params[key] = value === 'true';
  }
}

// Send a trimmed text or number only when the field is filled in.
function setTrimmedParam(params, key, value) {
  const trimmed = String(value ?? '').trim();
  if (trimmed) params[key] = trimmed;
}

// Full-width heading that groups the filter controls below it.
function FilterSection({ title }) {
  return (
    <Grid xs={12}>
      <Typography level="title-sm" textAlign="start" sx={{ mt: 1.5 }}>
        {title}
      </Typography>
    </Grid>
  );
}

// Any / yes / no select used for boolean rule flags.
function BooleanFilter({ label, value, onChange, anyLabel, yesLabel, noLabel }) {
  return (
    <Grid xs={12} sm={6} md={3}>
      <Typography level="body-xs" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Select value={value} onChange={(_, next) => onChange(next || '')} sx={{ width: '100%' }}>
        <Option value="">{anyLabel}</Option>
        <Option value="true">{yesLabel}</Option>
        <Option value="false">{noLabel}</Option>
      </Select>
    </Grid>
  );
}

// Text, number, or date input that stays empty until the user fills it.
function TextFilter({ label, value, onChange, type = 'text' }) {
  return (
    <Grid xs={12} sm={6} md={3}>
      <CustomTextField
        label={label}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        slotProps={type === 'number' ? { input: { step: 'any' } } : undefined}
        fullWidth
      />
    </Grid>
  );
}

export default function PricingRulesManagerScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState(null);
  const [results, setResults] = useState([]);
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [successDelete, setSuccessDelete] = useState(false);
  const [failedDelete, setFailedDelete] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState('');
  const [showCreatedSnack, setShowCreatedSnack] = useState(false);
  const [showDuplicatedSnack, setShowDuplicatedSnack] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateSourceRow, setDuplicateSourceRow] = useState(null);
  const [searchField, setSearchField] = useState('');
  // Extra field filters stay collapsed until the user opens them.
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [scopeFilter, setScopeFilter] = useState('');
  const [strategyFilter, setStrategyFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [menuFilter, setMenuFilter] = useState('');
  const [appliesToGroupFilter, setAppliesToGroupFilter] = useState('');
  // Dedicated field filters, sent only when the user fills them in.
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [nameArFilter, setNameArFilter] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [descriptionArFilter, setDescriptionArFilter] = useState('');
  const [notesFilter, setNotesFilter] = useState('');
  const [customerGroupFilter, setCustomerGroupFilter] = useState('');
  const [tariffCodeFilter, setTariffCodeFilter] = useState('');
  const [categoryCodeFilter, setCategoryCodeFilter] = useState('');
  const [menuLabelEnFilter, setMenuLabelEnFilter] = useState('');
  const [menuLabelArFilter, setMenuLabelArFilter] = useState('');
  const [menuOrderFilter, setMenuOrderFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [unitPriceFilter, setUnitPriceFilter] = useState('');
  const [totalPriceFilter, setTotalPriceFilter] = useState('');
  const [ceilStepFilter, setCeilStepFilter] = useState('');
  const [percentFilter, setPercentFilter] = useState('');
  const [minLinePriceFilter, setMinLinePriceFilter] = useState('');
  const [thresholdCbmFilter, setThresholdCbmFilter] = useState('');
  const [cbmFromFilter, setCbmFromFilter] = useState('');
  const [cbmToFilter, setCbmToFilter] = useState('');
  const [weightFromFilter, setWeightFromFilter] = useState('');
  const [weightToFilter, setWeightToFilter] = useState('');
  const [planFilter, setPlanFilter] = useState(null);
  const [settlementGroupFilter, setSettlementGroupFilter] = useState(null);
  const [customerFilter, setCustomerFilter] = useState(null);
  const [shipmentTypeFilter, setShipmentTypeFilter] = useState(null);
  const [unitFilter, setUnitFilter] = useState(null);
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [modifiedAfter, setModifiedAfter] = useState('');
  const [modifiedBefore, setModifiedBefore] = useState('');
  const [effectiveFromAfter, setEffectiveFromAfter] = useState('');
  const [effectiveFromBefore, setEffectiveFromBefore] = useState('');
  const [effectiveToAfter, setEffectiveToAfter] = useState('');
  const [effectiveToBefore, setEffectiveToBefore] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const fetchData = useCallback(
    async (isSearch = false) => {
      if (!hasPermission(PERMISSIONS.PRICING_RULES_LIST)) {
        setResults([]);
        setData(null);
        return;
      }
      if (isSearch) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
      setIsLoading(true);
      try {
        const page = isSearch ? 1 : paginationModel.page + 1;
        const params = {
          page,
          page_size: paginationModel.pageSize,
          ordering: '-id',
        };
        if (searchField.trim()) params.search = searchField.trim();
        if (scopeFilter) params.scope = scopeFilter;
        if (strategyFilter) params.strategy = strategyFilter;
        setBooleanParam(params, 'active', activeFilter);
        setBooleanParam(params, 'show_in_menu', menuFilter);
        setBooleanParam(params, 'applies_to_group', appliesToGroupFilter);
        // Text contains filters match the rule fields one by one.
        setTrimmedParam(params, 'code', codeFilter);
        setTrimmedParam(params, 'name', nameFilter);
        setTrimmedParam(params, 'name_ar', nameArFilter);
        setTrimmedParam(params, 'description', descriptionFilter);
        setTrimmedParam(params, 'description_ar', descriptionArFilter);
        setTrimmedParam(params, 'notes', notesFilter);
        setTrimmedParam(params, 'customer_group', customerGroupFilter);
        setTrimmedParam(params, 'tariff_code', tariffCodeFilter);
        setTrimmedParam(params, 'category_code', categoryCodeFilter);
        setTrimmedParam(params, 'menu_label_en', menuLabelEnFilter);
        setTrimmedParam(params, 'menu_label_ar', menuLabelArFilter);
        // Exact numbers, relations, and inclusive calendar days.
        setTrimmedParam(params, 'menu_order', menuOrderFilter);
        setTrimmedParam(params, 'priority', priorityFilter);
        setTrimmedParam(params, 'unit_price', unitPriceFilter);
        setTrimmedParam(params, 'total_price', totalPriceFilter);
        setTrimmedParam(params, 'ceil_step', ceilStepFilter);
        setTrimmedParam(params, 'percent', percentFilter);
        setTrimmedParam(params, 'min_line_price', minLinePriceFilter);
        setTrimmedParam(params, 'threshold_cbm', thresholdCbmFilter);
        setTrimmedParam(params, 'cbm_from', cbmFromFilter);
        setTrimmedParam(params, 'cbm_to', cbmToFilter);
        setTrimmedParam(params, 'weight_from', weightFromFilter);
        setTrimmedParam(params, 'weight_to', weightToFilter);
        if (planFilter?.id != null) params.plan = planFilter.id;
        if (settlementGroupFilter?.id != null) {
          params.settlement_group = settlementGroupFilter.id;
        }
        if (customerFilter?.id != null) params.customer = customerFilter.id;
        if (shipmentTypeFilter?.id != null) params.shipment_type = shipmentTypeFilter.id;
        if (unitFilter?.id != null) params.shipment_unit = unitFilter.id;
        if (createdAfter) params.created_at_after = createdAfter;
        if (createdBefore) params.created_at_before = createdBefore;
        if (modifiedAfter) params.modified_at_after = modifiedAfter;
        if (modifiedBefore) params.modified_at_before = modifiedBefore;
        if (effectiveFromAfter) params.effective_from_after = effectiveFromAfter;
        if (effectiveFromBefore) params.effective_from_before = effectiveFromBefore;
        if (effectiveToAfter) params.effective_to_after = effectiveToAfter;
        if (effectiveToBefore) params.effective_to_before = effectiveToBefore;
        const response = await listPricingRules(params);
        const actualData = response.data;
        setData(actualData);
        const list = Array.isArray(actualData)
          ? actualData
          : Array.isArray(actualData?.results)
            ? actualData.results
            : [];
        setResults(list);
      } catch (err) {
        console.error(err.message);
        setResults([]);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    },
    [
      hasPermission,
      searchField,
      scopeFilter,
      strategyFilter,
      activeFilter,
      menuFilter,
      appliesToGroupFilter,
      codeFilter,
      nameFilter,
      nameArFilter,
      descriptionFilter,
      descriptionArFilter,
      notesFilter,
      customerGroupFilter,
      tariffCodeFilter,
      categoryCodeFilter,
      menuLabelEnFilter,
      menuLabelArFilter,
      menuOrderFilter,
      priorityFilter,
      unitPriceFilter,
      totalPriceFilter,
      ceilStepFilter,
      percentFilter,
      minLinePriceFilter,
      thresholdCbmFilter,
      cbmFromFilter,
      cbmToFilter,
      weightFromFilter,
      weightToFilter,
      planFilter,
      settlementGroupFilter,
      customerFilter,
      shipmentTypeFilter,
      unitFilter,
      createdAfter,
      createdBefore,
      modifiedAfter,
      modifiedBefore,
      effectiveFromAfter,
      effectiveFromBefore,
      effectiveToAfter,
      effectiveToBefore,
      paginationModel.page,
      paginationModel.pageSize,
    ]
  );

  useEffect(() => {
    fetchData();
    // Pagination change only — filter submit remains handleSubmitFilter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleSubmitFilter = async (event) => {
    event.preventDefault();
    await fetchData(true);
  };

  const openDuplicateDialog = (row) => {
    if (!hasPermission(PERMISSIONS.PRICING_RULES_CREATE)) return;
    setDuplicateSourceRow(row);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateSuccess = async (copy) => {
    setShowDuplicatedSnack(true);
    await fetchData(true);
    window.open(`/PricingRules/${copy.id}/Edit`, '_blank');
  };

  const deleteRow = async (row) => {
    if (!hasPermission(PERMISSIONS.PRICING_RULES_DELETE)) return;
    setDeleteLoading(true);
    try {
      const response = await deletePricingRule(row.id);
      if (response.status === 200 || response.status === 204) {
        setResults((prev) => prev.filter((item) => item.id !== row.id));
        setSuccessDelete(true);
      } else {
        setFailedDelete(true);
        setDeleteErrorMsg(
          t('pricingRules.deleteFailedDetail', {
            detail: response.data?.detail ? ` ${response.data.detail}` : '',
          })
        );
      }
    } catch (e) {
      setFailedDelete(true);
      setDeleteErrorMsg(masterApiErrorMessage(e, t) || e.message);
    }
    setDeleteLoading(false);
    setDeleteAlert(false);
  };

  const pricingRuleJustCreatedFlag = Boolean(
    location.state && location.state.pricingRuleJustCreated
  );

  useEffect(() => {
    if (!pricingRuleJustCreatedFlag) return;
    setShowCreatedSnack(true);
    fetchData(true);
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: {} }
    );
  }, [
    pricingRuleJustCreatedFlag,
    fetchData,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);

  const columns = useMemo(
    () => [
      { field: 'id', headerName: t('trackingEvent.colId'), flex: 1, minWidth: 70 },
      { field: 'code', headerName: t('pricingRules.colCode'), flex: 1.8, minWidth: 140 },
      { field: 'name', headerName: t('pricingRules.colName'), flex: 2.2, minWidth: 160 },
      {
        field: 'scope',
        headerName: t('pricingRules.colScope'),
        flex: 1.2,
        minWidth: 100,
      },
      {
        field: 'strategy',
        headerName: t('pricingRules.colStrategy'),
        flex: 1.8,
        minWidth: 150,
      },
      {
        // Matches shipment pricing_tariff_code.
        field: 'tariff_code',
        headerName: t('pricingRules.colTariffCode'),
        flex: 1.4,
        minWidth: 170,
        valueGetter: (a, b) => dashIfEmpty(valueGetterRow(a, b)?.tariff_code),
      },
      {
        // Matches shipment product_category_code.
        field: 'category_code',
        headerName: t('pricingRules.colCategoryCode'),
        flex: 1.5,
        minWidth: 190,
        valueGetter: (a, b) => dashIfEmpty(valueGetterRow(a, b)?.category_code),
      },
      {
        field: 'unit_price',
        headerName: t('pricingRules.colUnitPrice'),
        flex: 1,
        minWidth: 110,
        sortable: false,
        renderCell: (params) => <OmrAmount value={params.row.unit_price} empty="—" />,
      },
      {
        field: 'total_price',
        headerName: t('pricingRules.colTotalPrice'),
        flex: 1,
        minWidth: 110,
        sortable: false,
        renderCell: (params) => <OmrAmount value={params.row.total_price} empty="—" />,
      },
      {
        field: 'percent',
        headerName: t('pricingRules.colPercent'),
        flex: 0.8,
        minWidth: 80,
        valueGetter: (a, b) => formatPercent(valueGetterRow(a, b)?.percent),
      },
      {
        field: 'min_line_price',
        headerName: t('pricingRules.colMinLinePrice'),
        flex: 1,
        minWidth: 110,
        sortable: false,
        renderCell: (params) => <OmrAmount value={params.row.min_line_price} empty="—" />,
      },
      {
        field: 'customer',
        headerName: t('pricingRules.colCustomer'),
        flex: 1.4,
        minWidth: 140,
        sortable: false,
        valueGetter: (a, b) => formatCustomerRef(valueGetterRow(a, b)?.customer),
      },
      {
        field: 'shipment_type',
        headerName: t('pricingRules.colShipmentType'),
        flex: 1.2,
        minWidth: 120,
        sortable: false,
        valueGetter: (a, b) => formatShipmentTypeRef(valueGetterRow(a, b)),
      },
      {
        field: 'shipment_unit_serial_number',
        headerName: t('pricingRules.colShipmentUnit'),
        flex: 1.2,
        minWidth: 130,
        sortable: false,
        valueGetter: (a, b) => dashIfEmpty(valueGetterRow(a, b)?.shipment_unit_serial_number),
      },
      {
        field: 'destination_city',
        headerName: t('pricingRules.colDestinationCity'),
        flex: 1.1,
        minWidth: 120,
        valueGetter: (a, b) => dashIfEmpty(valueGetterRow(a, b)?.destination_city),
      },
      {
        field: 'plan_code',
        headerName: t('pricingRules.colPlanCode'),
        flex: 1.2,
        minWidth: 110,
        valueGetter: (a, b) => dashIfEmpty(valueGetterRow(a, b)?.plan_code),
      },
      {
        field: 'plan_name',
        headerName: t('pricingRules.colPlanName'),
        flex: 1.4,
        minWidth: 130,
        valueGetter: (a, b) => dashIfEmpty(valueGetterRow(a, b)?.plan_name),
      },
      {
        field: 'priority',
        headerName: t('pricingRules.colPriority'),
        flex: 0.9,
        minWidth: 90,
      },
      {
        field: 'active',
        headerName: t('pricingRules.colActive'),
        flex: 0.8,
        minWidth: 80,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.active ? t('serviceManager.priceYes') : t('serviceManager.priceNo');
        },
      },
      {
        field: 'actions',
        headerName: t('trackingEvent.colActions'),
        flex: 2.5,
        minWidth: 160,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <PermissionGate permission={PERMISSIONS.PRICING_RULES_UPDATE}>
              <Tooltip title={t('pricingRules.tooltipEdit')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/PricingRules/${params.row.id}/Edit`, '_blank');
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </PermissionGate>
            <PermissionGate permission={PERMISSIONS.PRICING_RULES_CREATE}>
              <Tooltip title={t('pricingRules.tooltipDuplicate')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="neutral"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDuplicateDialog(params.row);
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </PermissionGate>
            <PermissionGate permission={PERMISSIONS.PRICING_RULES_DELETE}>
              <Tooltip title={t('pricingRules.tooltipDelete')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRow(params.row);
                    setDeleteAlert(true);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </PermissionGate>
          </Box>
        ),
      },
    ],
    [t]
  );

  const rowCount = data?.count != null ? data.count : results.length;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <form onSubmit={handleSubmitFilter}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width="100%">
            <Box>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                <Link color="neutral" href="/PricingRules">
                  {t('nav.pricingRules')}
                </Link>
                <Link color="neutral" href="/PricingRules">
                  {t('pricingRules.breadcrumbManager')}
                </Link>
              </Breadcrumbs>
              <Typography textAlign="start" level="h1">
                {t('pricingRules.pageTitle')}
              </Typography>
            </Box>

            <PermissionGate permissions={[PERMISSIONS.PRICING_RULES_LIST]}>
              <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1, mt: 1 }}>
                <Grid xs={12}>
                  <Typography level="h3" textAlign="start">
                    {t('pricingRules.filterHeading')}
                  </Typography>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <CustomTextField
                    label={t('pricingRules.searchLabel')}
                    type="text"
                    value={searchField}
                    onChange={(e) => setSearchField(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Typography level="body-xs" sx={{ mb: 0.5 }}>
                    {t('pricingRules.scopeFilter')}
                  </Typography>
                  <Select
                    value={scopeFilter}
                    onChange={(_, v) => setScopeFilter(v || '')}
                    sx={{ width: '100%' }}
                  >
                    <Option value="">{t('pricingRules.scopeAny')}</Option>
                    <Option value="base">base</Option>
                    <Option value="addon">addon</Option>
                    <Option value="manual">manual</Option>
                  </Select>
                </Grid>
                <BooleanFilter
                  label={t('pricingRules.activeFilter')}
                  value={activeFilter}
                  onChange={setActiveFilter}
                  anyLabel={t('pricingRules.activeAny')}
                  yesLabel={t('serviceManager.priceYes')}
                  noLabel={t('serviceManager.priceNo')}
                />
                <Grid xs={12} sm={6} md={3}>
                  <ShipmentUnitOptionList
                    label={t('pricingRules.unitFilter')}
                    defaultValue={unitFilter}
                    onChange={(_, v) => setUnitFilter(v)}
                  />
                </Grid>
                <Grid xs={12}>
                  <Button
                    type="button"
                    variant="plain"
                    size="sm"
                    color="neutral"
                    startDecorator={<FilterListIcon />}
                    endDecorator={moreFiltersOpen ? <ExpandLess /> : <ExpandMore />}
                    onClick={() => setMoreFiltersOpen((open) => !open)}
                  >
                    {moreFiltersOpen
                      ? t('customerList.collapseFilters')
                      : t('customerList.moreFilters')}
                  </Button>
                </Grid>
                {moreFiltersOpen ? (
                  <>
                <FilterSection title={t('pricingRules.filterSectionIdentity')} />
                <TextFilter
                  label={t('pricingRules.fieldCode')}
                  value={codeFilter}
                  onChange={setCodeFilter}
                />
                <TextFilter
                  label={t('pricingRules.fieldName')}
                  value={nameFilter}
                  onChange={setNameFilter}
                />
                <TextFilter
                  label={t('pricingRules.fieldNameAr')}
                  value={nameArFilter}
                  onChange={setNameArFilter}
                />
                <TextFilter
                  label={t('pricingRules.fieldDescription')}
                  value={descriptionFilter}
                  onChange={setDescriptionFilter}
                />
                <TextFilter
                  label={t('pricingRules.fieldDescriptionAr')}
                  value={descriptionArFilter}
                  onChange={setDescriptionArFilter}
                />
                <TextFilter
                  label={t('pricingRules.fieldNotes')}
                  value={notesFilter}
                  onChange={setNotesFilter}
                />
                <FilterSection title={t('pricingRules.filterSectionRelations')} />
                <Grid xs={12} sm={6} md={3}>
                  <PricingPlanOptionList
                    label={t('pricingRules.fieldPlan')}
                    defaultValue={planFilter}
                    activeOnly={false}
                    onChange={(_, v) => setPlanFilter(v)}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <SettlementGroupOptionList
                    label={t('pricingRules.fieldSettlementGroup')}
                    defaultValue={settlementGroupFilter}
                    activeOnly={false}
                    onChange={(_, v) => setSettlementGroupFilter(v)}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomerOptionList
                    label={t('pricingRules.fieldCustomer')}
                    defaultValue={customerFilter}
                    onChange={(_, v) => setCustomerFilter(v)}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <ShipmentTypeOptionList
                    label={t('pricingRules.fieldShipmentType')}
                    defaultValue={shipmentTypeFilter}
                    onChange={(_, v) => setShipmentTypeFilter(v)}
                  />
                </Grid>
                <FilterSection title={t('pricingRules.filterSectionMatch')} />
                <BooleanFilter
                  label={t('pricingRules.fieldAppliesToGroup')}
                  value={appliesToGroupFilter}
                  onChange={setAppliesToGroupFilter}
                  anyLabel={t('pricingRules.scopeAny')}
                  yesLabel={t('serviceManager.priceYes')}
                  noLabel={t('serviceManager.priceNo')}
                />
                <TextFilter
                  label={t('pricingRules.fieldCustomerGroup')}
                  value={customerGroupFilter}
                  onChange={setCustomerGroupFilter}
                />
                <TextFilter
                  label={t('pricingRules.fieldTariffCode')}
                  value={tariffCodeFilter}
                  onChange={setTariffCodeFilter}
                />
                <TextFilter
                  label={t('pricingRules.fieldCategoryCode')}
                  value={categoryCodeFilter}
                  onChange={setCategoryCodeFilter}
                />
                <TextFilter
                  label={t('pricingRules.fieldCbmFrom')}
                  value={cbmFromFilter}
                  onChange={setCbmFromFilter}
                  type="number"
                />
                <TextFilter
                  label={t('pricingRules.fieldCbmTo')}
                  value={cbmToFilter}
                  onChange={setCbmToFilter}
                  type="number"
                />
                <TextFilter
                  label={t('pricingRules.fieldWeightFrom')}
                  value={weightFromFilter}
                  onChange={setWeightFromFilter}
                  type="number"
                />
                <TextFilter
                  label={t('pricingRules.fieldWeightTo')}
                  value={weightToFilter}
                  onChange={setWeightToFilter}
                  type="number"
                />
                <FilterSection title={t('pricingRules.filterSectionStrategy')} />
                <Grid xs={12} sm={6} md={3}>
                  <Typography level="body-xs" sx={{ mb: 0.5 }}>
                    {t('pricingRules.fieldStrategy')}
                  </Typography>
                  <Select
                    value={strategyFilter}
                    onChange={(_, v) => setStrategyFilter(v || '')}
                    sx={{ width: '100%' }}
                  >
                    <Option value="">{t('pricingRules.strategyAny')}</Option>
                    {STRATEGY_OPTIONS.map((slug) => (
                      <Option key={slug} value={slug}>
                        {slug}
                      </Option>
                    ))}
                  </Select>
                </Grid>
                <TextFilter
                  label={t('pricingRules.fieldUnitPrice')}
                  value={unitPriceFilter}
                  onChange={setUnitPriceFilter}
                  type="number"
                />
                <TextFilter
                  label={t('pricingRules.fieldTotalPrice')}
                  value={totalPriceFilter}
                  onChange={setTotalPriceFilter}
                  type="number"
                />
                <TextFilter
                  label={t('pricingRules.fieldCeilStep')}
                  value={ceilStepFilter}
                  onChange={setCeilStepFilter}
                  type="number"
                />
                <TextFilter
                  label={t('pricingRules.fieldPercent')}
                  value={percentFilter}
                  onChange={setPercentFilter}
                  type="number"
                />
                <TextFilter
                  label={t('pricingRules.fieldMinLinePrice')}
                  value={minLinePriceFilter}
                  onChange={setMinLinePriceFilter}
                  type="number"
                />
                <TextFilter
                  label={t('pricingRules.fieldThresholdCbm')}
                  value={thresholdCbmFilter}
                  onChange={setThresholdCbmFilter}
                  type="number"
                />
                <TextFilter
                  label={t('pricingRules.fieldPriority')}
                  value={priorityFilter}
                  onChange={setPriorityFilter}
                  type="number"
                />
                <FilterSection title={t('pricingRules.filterSectionMenu')} />
                <BooleanFilter
                  label={t('pricingRules.fieldShowInMenu')}
                  value={menuFilter}
                  onChange={setMenuFilter}
                  anyLabel={t('pricingRules.menuAny')}
                  yesLabel={t('serviceManager.priceYes')}
                  noLabel={t('serviceManager.priceNo')}
                />
                <TextFilter
                  label={t('pricingRules.fieldMenuLabelEn')}
                  value={menuLabelEnFilter}
                  onChange={setMenuLabelEnFilter}
                />
                <TextFilter
                  label={t('pricingRules.fieldMenuLabelAr')}
                  value={menuLabelArFilter}
                  onChange={setMenuLabelArFilter}
                />
                <TextFilter
                  label={t('pricingRules.fieldMenuOrder')}
                  value={menuOrderFilter}
                  onChange={setMenuOrderFilter}
                  type="number"
                />
                <FilterSection title={t('pricingRules.filterSectionDates')} />
                <TextFilter
                  label={t('pricingRules.filterCreatedFrom')}
                  value={createdAfter}
                  onChange={setCreatedAfter}
                  type="date"
                />
                <TextFilter
                  label={t('pricingRules.filterCreatedTo')}
                  value={createdBefore}
                  onChange={setCreatedBefore}
                  type="date"
                />
                <TextFilter
                  label={t('pricingRules.filterModifiedFrom')}
                  value={modifiedAfter}
                  onChange={setModifiedAfter}
                  type="date"
                />
                <TextFilter
                  label={t('pricingRules.filterModifiedTo')}
                  value={modifiedBefore}
                  onChange={setModifiedBefore}
                  type="date"
                />
                <TextFilter
                  label={t('pricingRules.filterEffectiveFromAfter')}
                  value={effectiveFromAfter}
                  onChange={setEffectiveFromAfter}
                  type="date"
                />
                <TextFilter
                  label={t('pricingRules.filterEffectiveFromBefore')}
                  value={effectiveFromBefore}
                  onChange={setEffectiveFromBefore}
                  type="date"
                />
                <TextFilter
                  label={t('pricingRules.filterEffectiveToAfter')}
                  value={effectiveToAfter}
                  onChange={setEffectiveToAfter}
                  type="date"
                />
                <TextFilter
                  label={t('pricingRules.filterEffectiveToBefore')}
                  value={effectiveToBefore}
                  onChange={setEffectiveToBefore}
                  type="date"
                />
                  </>
                ) : null}
              </Grid>
              <Button type="submit" loading={isLoading}>
                {t('actions.search')}
              </Button>
            </PermissionGate>

            <Grid mt={2} container spacing={2} justifyContent="flex-end">
              <PermissionGate permission={PERMISSIONS.PRICING_RULES_IMPORT}>
                <Grid>
                  <Tooltip title={t('pricingRules.tooltipImport')}>
                    <IconButton
                      onClick={() => navigate('/PricingRules/Import')}
                      color="neutral"
                      size="large"
                      sx={{ width: { xs: '100%', md: 'auto' } }}
                    >
                      <UploadFile sx={{ fontSize: 25 }} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.PRICING_RULES_CREATE}>
                <Grid>
                  <Tooltip title={t('pricingRules.tooltipAdd')}>
                    <IconButton
                      onClick={() => navigate('/PricingRules/Add')}
                      color="primary"
                      size="large"
                      sx={{
                        width: { xs: '100%', md: 'auto' },
                      }}
                    >
                      <Add sx={{ fontSize: 25 }} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </PermissionGate>
            </Grid>

            <PermissionGate permissions={[PERMISSIONS.PRICING_RULES_LIST]}>
              <Box sx={{ height: '70vh', width: '100%', mt: 2 }}>
                <DataGrid
                  rows={results}
                  columns={columns}
                  pagination
                  paginationMode="server"
                  rowCount={rowCount}
                  paginationModel={paginationModel}
                  pageSizeOptions={[25, 50, 100]}
                  onPaginationModelChange={setPaginationModel}
                  loading={isLoading}
                />
              </Box>
            </PermissionGate>

            <DeleteAlert
              open={deleteAlert}
              onClose={() => setDeleteAlert(false)}
              onDelete={() => selectedRow && deleteRow(selectedRow)}
              loading={deleteLoading}
              title={t('pricingRules.deleteConfirmTitle')}
              body={t('pricingRules.deleteConfirmBody')}
              confirmLabel={t('actions.delete')}
            />

            <DuplicatePricingRuleDialog
              open={duplicateDialogOpen}
              onClose={() => setDuplicateDialogOpen(false)}
              sourceRule={duplicateSourceRow}
              onSuccess={handleDuplicateSuccess}
            />

            <Snackbar
              open={successDelete}
              autoHideDuration={3000}
              onClose={() => setSuccessDelete(false)}
              color="success"
              variant="soft"
            >
              {t('pricingRules.deletedSuccess')}
            </Snackbar>
            <Snackbar
              open={showCreatedSnack}
              autoHideDuration={3500}
              onClose={() => setShowCreatedSnack(false)}
              color="success"
              variant="soft"
            >
              {t('pricingRules.snackbarCreated')}
            </Snackbar>
            <Snackbar
              open={showDuplicatedSnack}
              autoHideDuration={4500}
              onClose={() => setShowDuplicatedSnack(false)}
              color="success"
              variant="soft"
            >
              {t('pricingRules.snackbarDuplicated')}
            </Snackbar>
            <Snackbar
              open={failedDelete}
              autoHideDuration={6000}
              onClose={() => setFailedDelete(false)}
              color="danger"
              variant="soft"
            >
              {deleteErrorMsg || t('pricingRules.deleteError')}
            </Snackbar>
          </Box>
        </Box>
      </form>
    </ThemeProvider>
  );
}
